import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import {
  AppIcon,
  AppIconNames,
  Button,
  ButtonIconPosition,
  ButtonVariant,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import { Table } from 'src/components/organisms'
import { Column, Data } from 'src/components/organisms/table/types'

import {
  hasPortfolioNewsArticleUrl,
  paragraphsFromPortfolioNewsEmailBody,
} from '../domain/portfolio-news-article-body'
import { usePortfolioNewsTableColumns } from '../hooks/use-portfolio-news-table-columns'
import type { IPortfolioNewsArticleEntity } from '../types'
import { RelatedNewsMobileList } from './components/related-news-mobile-list'

const asPortfolioNewsArticle = (row: Data): IPortfolioNewsArticleEntity =>
  row as unknown as IPortfolioNewsArticleEntity

interface IPortfolioNewsDetailTemplateProps {
  article: IPortfolioNewsArticleEntity
  relatedArticles: IPortfolioNewsArticleEntity[]
}

const backLinkClassName =
  'flex w-fit items-center gap-2 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent'

// pt-11 matches Figma's 44px top inset. The bottom padding adds the iOS Safari
// home-indicator inset on top of the same 44px floor so the last row of "More
// recent news" stays reachable when Safari's bottom toolbar collapses/expands.
const pageContainerClassName =
  'mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-6 pt-11 pb-[calc(theme(spacing.11)+env(safe-area-inset-bottom))] md:px-12 xl:px-0'

const ReadFullArticleButton = ({
  articleUrl,
  t,
}: {
  articleUrl: string
  t: TFunction
}): JSX.Element => (
  <Button
    type="button"
    variant={ButtonVariant.primary}
    className="self-start"
    icon={
      <AppIcon
        name={AppIconNames.externalLink}
        size={16}
        className="text-inherit"
        alt=""
      />
    }
    iconPosition={ButtonIconPosition.right}
    label={t('portfolioNews.detail.readFullArticle')}
    aria-label={t('portfolioNews.detail.readFullArticleAria')}
    onClick={(): void => {
      window.open(articleUrl, '_blank', 'noopener,noreferrer')
    }}
  />
)

const BackToListLink = ({ t }: { t: TFunction }): JSX.Element => {
  const navigate = useNavigate()

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    const canGoBack =
      typeof window !== 'undefined' && window.history.state?.idx > 0
    if (canGoBack) {
      event.preventDefault()
      navigate(-1)
    }
  }

  return (
    <Link
      to="/portfolio-news"
      onClick={handleClick}
      className={backLinkClassName}
    >
      <AppIcon
        name={AppIconNames.arrowLeft}
        size={16}
        className="text-content-primary"
        alt=""
      />
      <Typography
        variant={TypographyVariant.bodySmStrong}
        className="text-content-primary"
      >
        {t('portfolioNews.detail.backLink')}
      </Typography>
    </Link>
  )
}

const ArticleMetaRow = ({
  article,
}: {
  article: Pick<
    IPortfolioNewsArticleEntity,
    'company' | 'source' | 'publishedDisplay'
  >
}): JSX.Element => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
    <Typography
      variant={TypographyVariant.bodyXsStrong}
      className="text-brand-accent"
    >
      {article.company}
    </Typography>
    <span className="inline-block h-3 w-px shrink-0 bg-border" aria-hidden />
    <Typography
      variant={TypographyVariant.bodyXs}
      className="text-content-secondary"
    >
      {article.source}
    </Typography>
    <span className="inline-block h-3 w-px shrink-0 bg-border" aria-hidden />
    <Typography
      variant={TypographyVariant.bodyXs}
      className="text-content-secondary"
    >
      {article.publishedDisplay}
    </Typography>
  </div>
)

const ArticleTitleBlock = ({ title }: { title: string }): JSX.Element => (
  <div className="flex flex-col gap-4">
    <Typography
      variant={TypographyVariant.titleXl}
      weight={TypographyWeight.light}
      className="text-content-primary"
    >
      {title}
    </Typography>
    <div className="h-px w-full bg-content-secondary" />
  </div>
)

const AiSummaryBlock = ({
  articleId,
  bullets,
  t,
}: {
  articleId: string
  bullets: string[]
  t: TFunction
}): JSX.Element => (
  <div className="flex flex-col items-start gap-6">
    <div className="flex items-center justify-center gap-[10px] rounded-lg border border-content-secondary px-3 py-2">
      <Typography
        variant={TypographyVariant.bodyXxsStrong}
        className="uppercase text-content-secondary"
      >
        {t('portfolioNews.detail.aiSummaryBadge')}
      </Typography>
    </div>
    <ul className="flex list-disc flex-col gap-2 pl-6">
      {bullets.map((bullet, index) => (
        <li
          key={`${articleId}-bullet-${index}`}
          className="marker:text-content-primary"
        >
          <Typography
            variant={TypographyVariant.bodyLg}
            className="text-justify text-content-primary"
          >
            {bullet}
          </Typography>
        </li>
      ))}
    </ul>
  </div>
)

const FullArticleDivider = ({ label }: { label: string }): JSX.Element => (
  <div className="flex items-center gap-4">
    <div className="h-px min-h-px flex-1 bg-content-secondary" />
    <Typography
      variant={TypographyVariant.bodyXxsStrong}
      className="shrink-0 uppercase text-content-secondary"
    >
      {label}
    </Typography>
    <div className="h-px min-h-px flex-1 bg-content-secondary" />
  </div>
)

const InlineFullArticle = ({
  articleId,
  paragraphs,
  t,
}: {
  articleId: string
  paragraphs: string[]
  t: TFunction
}): JSX.Element => (
  <div className="flex flex-col gap-8">
    <FullArticleDivider label={t('portfolioNews.detail.fullArticleLabel')} />
    <div className="flex flex-col gap-4">
      {paragraphs.map((paragraph, index) => (
        <Typography
          key={`${articleId}-p-${index}`}
          variant={TypographyVariant.bodyLg}
          className="text-justify text-content-primary"
        >
          {paragraph}
        </Typography>
      ))}
    </div>
  </div>
)

const RelatedNewsSection = ({
  columns,
  rows,
  onRowClick,
  t,
}: {
  columns: Column[]
  rows: IPortfolioNewsArticleEntity[]
  onRowClick: (article: IPortfolioNewsArticleEntity) => void
  t: TFunction
}): JSX.Element => (
  <section className="flex flex-col gap-6">
    <Typography
      variant={TypographyVariant.titleSm}
      weight={TypographyWeight.light}
      className="text-content-primary"
    >
      {t('portfolioNews.detail.moreRecentNews')}
    </Typography>

    <div className="lg:hidden">
      <RelatedNewsMobileList
        articles={rows}
        onArticleClick={onRowClick}
        ariaLabel={t('portfolioNews.detail.moreRecentNews')}
      />
    </div>

    <div className="hidden overflow-x-auto lg:block">
      <Table
        columns={columns}
        data={rows as unknown as Data[]}
        idPropertyName="id"
        onRowClick={row => onRowClick(asPortfolioNewsArticle(row))}
      />
    </div>
  </section>
)

export const PortfolioNewsDetailTemplate = ({
  article,
  relatedArticles,
}: IPortfolioNewsDetailTemplateProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const relatedColumns = usePortfolioNewsTableColumns()
  const showReadFullArticleLink = hasPortfolioNewsArticleUrl(article.articleUrl)
  const inlineParagraphs = paragraphsFromPortfolioNewsEmailBody(
    article.emailBody,
  )

  const handleRelatedRowClick = (
    relatedArticle: IPortfolioNewsArticleEntity,
  ): void => {
    navigate(`/portfolio-news/${relatedArticle.id}`)
  }

  return (
    <main className="bg-surface-page">
      <div className={pageContainerClassName}>
        <BackToListLink t={t} />

        <div className="flex flex-col gap-5">
          <ArticleMetaRow article={article} />
          <ArticleTitleBlock title={article.articleName} />
        </div>

        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-6">
            <AiSummaryBlock
              articleId={article.id}
              bullets={article.summaryBullets}
              t={t}
            />

            {showReadFullArticleLink && article.articleUrl ? (
              <ReadFullArticleButton articleUrl={article.articleUrl} t={t} />
            ) : null}
            {inlineParagraphs.length > 0 ? (
              <InlineFullArticle
                articleId={article.id}
                paragraphs={inlineParagraphs}
                t={t}
              />
            ) : null}
          </div>

          {relatedArticles.length > 0 ? (
            <RelatedNewsSection
              columns={relatedColumns}
              rows={relatedArticles}
              onRowClick={handleRelatedRowClick}
              t={t}
            />
          ) : null}
        </div>
      </div>
    </main>
  )
}
